import { useState, useEffect, useRef, useCallback } from "react";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import FilterModal from "../components/FilterModal";
import ContractorCard from "../components/ContractorCard";
import BottomNavbar from "../components/BottomNavbar";
import Header from "../components/Header";
import "./css/FindContractors.css";
import noWorkImage from "../assets/images/no_work.png";

export default function FindContractors() {
  const [profile, setProfile] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [filterData, setFilterData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const searchTimeoutRef = useRef(null);

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;
  const LIMIT = 20;

  // Translations
  const t = {
    en: {
      loading: "Loading",
      filter: "Filter",
      searchContractors: "Search Contractors",
      noContractors: "No contractors available",
      errorLoading: "Failed to load contractors",
    },
    ar: {
      loading: "جاري التحميل",
      filter: "تصفية",
      searchContractors: "ابحث عن المقاولين",
      noContractors: "لا يوجد مقاولون متاحون",
      errorLoading: "فشل تحميل المقاولين",
    },
  }[lang];

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profileData = data.response;

          // Set language preference
          if (profileData.language_preference) {
            localStorage.setItem("app_language", profileData.language_preference);
            setLang(profileData.language_preference);
          }

          setProfile(profileData);
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        if (err.code !== 404) {
          showToast(err.message || "Failed to load profile", "error");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [USER_BASE_URL, VERSION]);

  // Fetch contractors
  const fetchContractors = useCallback(
    async (page = 1, payload = null) => {
      try {
        setLoadingContractors(true);
        const url = `${USER_BASE_URL}/${VERSION}/contractor/fetch/?limit=${LIMIT}&page=${page}`;

        // Build payload - only include fields that have values
        const requestPayload = { ...payload };

        // Add search query if present
        if (searchQuery && searchQuery.trim()) {
          requestPayload.name = searchQuery.trim();
        }

        const bodyPayload = Object.keys(requestPayload).length > 0 ? requestPayload : {};
        const data = await apiFetch(
          url,
          "POST",
          JSON.stringify(bodyPayload),
          {
            "Content-Type": "application/json",
          }
        );

        if (data && data.response_code === 200) {
          const contractorsList = data.response?.contractors || [];

          if (page === 1) {
            setContractors(contractorsList);
          } else {
            setContractors((prev) => [...prev, ...contractorsList]);
          }
          setHasMorePages(contractorsList.length === LIMIT);
          setCurrentPage(page);
        }
      } catch (err) {
        console.error("Error fetching contractors:", err);
        showToast(err.message || t.errorLoading, "error");
      } finally {
        setLoadingContractors(false);
      }
    },
    [USER_BASE_URL, VERSION, searchQuery, t.errorLoading]
  );

  // Debounced search effect
  useEffect(() => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for 500ms
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }, 500);

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  // Fetch contractors when search query or filter changes
  useEffect(() => {
    if (!loading) {
      fetchContractors(1, filterData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterData]);

  // Initial fetch
  useEffect(() => {
    if (!loading && profile) {
      fetchContractors(1, filterData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, profile]);

  // Handle filter apply
  const handleFilterApply = (filterPayload) => {
    setFilterData(filterPayload);
    setCurrentPage(1);
    fetchContractors(1, filterPayload);
    setShowFilter(false);
  };

  // Handle search trigger (on Enter or search icon click)
  const handleSearchTrigger = () => {
    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    // Immediately set search query and trigger search
    setSearchQuery(searchInput);
    setCurrentPage(1);
  };

  // Handle Enter key press
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchTrigger();
    }
  };

  // Handle favorite update
  const handleFavoriteUpdate = (contractorId, isFavorite) => {
    setContractors((prev) =>
      prev.map((contractor) => {
        const currentId = String(contractor._id || "");
        const updateId = String(contractorId || "");
        return currentId === updateId
          ? { ...contractor, is_favorite: isFavorite }
          : contractor;
      })
    );
  };

  // Infinite scroll observer ref
  const observerTarget = useRef(null);

  // Handle infinite scroll
  const handleLoadMore = useCallback(() => {
    if (!loadingContractors && hasMorePages) {
      fetchContractors(currentPage + 1, filterData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingContractors, hasMorePages, currentPage, filterData, fetchContractors]);

  // Infinite scroll effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMorePages && !loadingContractors) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [handleLoadMore, hasMorePages, loadingContractors]);

  // Loading screen
  if (loading) {
    return (
      <div className="find-contractors-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  return (
    <div className="find-contractors-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <div className="search-bar-container">
          <input
            type="text"
            placeholder={t.searchContractors}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            className="search-input"
          />
          <button
            type="button"
            className="search-icon-btn"
            onClick={handleSearchTrigger}
            title={t.searchContractors}
          >
            <i className="bx bx-search search-icon"></i>
          </button>
        </div>
        <button
          className="filter-btn"
          onClick={() => setShowFilter(true)}
          title={t.filter}
        >
          <i className="bx bx-filter-alt"></i>
        </button>
      </div>

      {/* Main Content */}
      <div className="find-contractors-content">
        {loadingContractors && contractors.length === 0 ? (
          // Loading contractors
          <div className="find-contractors-loading-top">
            <div className="spinner"></div>
            <p>{t.loading}</p>
          </div>
        ) : contractors.length === 0 ? (
          // No contractors available
          <div className="no-contractors-container">
            <img
              src={noWorkImage}
              alt="No Contractors"
              className="no-contractors-image"
            />
            <p className="no-contractors-message">{t.noContractors}</p>
          </div>
        ) : (
          // Contractors list
          <div className="contractors-list">
            {contractors.map((contractor) => (
              <ContractorCard
                key={contractor._id}
                contractor={contractor}
                lang={lang}
                onFavoriteUpdate={handleFavoriteUpdate}
              />
            ))}
            {/* Infinite scroll trigger */}
            {hasMorePages && (
              <div ref={observerTarget} className="infinite-scroll-trigger">
                {loadingContractors && (
                  <div className="loading-more-contractors">
                    <div className="spinner"></div>
                    <span>{t.loading}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filter Modal */}
      {showFilter && profile && (
        <FilterModal
          isOpen={showFilter}
          onClose={() => setShowFilter(false)}
          onApply={handleFilterApply}
          profileSubCategories={profile.sub_categories || []}
          profileWorkAmount={profile.work_amount}
          currentFilterData={filterData}
          lang={lang}
        />
      )}

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="find" lang={lang} />
    </div>
  );
}

